import { randomIntFromChoices, randomIntFromInterval } from '~/bootcamp/Activities/utils/activities.utils'
import { range, shuffle } from '~/utils/helpers'

import { getRangesByDifficulty } from './constants'

/**
 * Responsible to generate final term after adjustment and shuffle
 * @param difficultyLevel
 * @param taskHistory
 * @returns terms
 */
export function generateTerms(difficultyLevel: number, taskHistory: number[]): number[] {
  const { unknownVariable, numRange, minResult, maxResult } = getRangesByDifficulty(difficultyLevel)

  const resultNum = randomIntFromInterval(minResult, maxResult, taskHistory)
  const terms = makeTerms(unknownVariable, numRange)
  const adjustedTerm = adjustEqnWithResult(terms, resultNum, numRange)
  const total = adjustedTerm.reduce((prev, curr) => prev + curr, 0)
  if (total !== resultNum) {
    return generateTerms(difficultyLevel, taskHistory)
  }
  return shuffle(adjustedTerm)
}

/**
 * Equation adjustment with result
 * @param terms left side plus minus expression
 * @param resultNum result of the equation
 * @param numRange generates the min max from this value range
 * @returns adjustment term
 */
export function adjustEqnWithResult(terms: number[], resultNum: number, numRange: number[]): number[] {
  const total = terms.reduce((prev, curr) => prev + curr, 0)
  let diff = resultNum - total
  return terms.map((item) => {
    const nMin = Math.random() > 0.5 ? numRange[numRange.length - 1] * -1 : numRange[numRange.length - 2] * -1
    const nMax = Math.random() > 0.5 ? numRange[0] * -1 : numRange[1] * -1
    const pMin = Math.random() > 0.5 ? numRange[0] : numRange[1]
    const pMax = Math.random() > 0.5 ? numRange[numRange.length - 1] : numRange[numRange.length - 2]

    if (item > 0 && diff > 0) {
      if (item + diff > pMax) {
        diff = item + diff - pMax
        item = pMax
      } else {
        item = item + diff
        diff = 0
      }
    } else if (item < 0 && diff > 0) {
      if (item + diff < nMax) {
        item = item + diff
        diff = 0
      } else {
        diff = diff + item - nMax
        item = nMax
      }
    } else if (item > 0 && diff < 0) {
      if (item + diff > pMin) {
        item = item + diff
        diff = 0
      } else {
        diff = diff + item - pMin
        item = pMin
      }
    } else if (item < 0 && diff < 0) {
      if (item + diff > nMin) {
        item = item + diff
        diff = 0
      } else {
        diff = item + diff - nMin
        item = nMin
      }
    }
    return item
  })
}

/**
 * Generates the plus minus equation.
 * @param unknownVariable number of variable like a, b, c ...
 * @param numRange list of value to generate the expression
 * @returns random term
 */
export function makeTerms(unknownVariable: number, numRange: number[]): number[] {
  const terms = range(unknownVariable).map(() => randomIntFromChoices(numRange))
  // Ensure that we have at least 1 negative term and 1 positive number
  const firstTerm = terms[0] * 1
  const secondTerm = terms[1] * -1
  const restOfTerms = terms.slice(2).map((elem) => (Math.random() > 0.5 ? elem * -1 : elem))
  const finalTerm = [firstTerm, secondTerm, ...restOfTerms]

  // Ensured total sum positive
  const total = finalTerm.reduce((prev, curr) => prev + curr, 0)
  if (total === 0) {
    return makeTerms(unknownVariable, numRange) // When total is equal to zero, regenerates the terms
  } else if (total < 0) {
    return finalTerm.map((item) => item * -1)
  }

  return finalTerm
}
